import * as Phaser from "phaser";
import {MoveCommand} from "../model/messages";

// Fixed-position virtual joystick for touch devices.
// Renders as Phaser GameObjects on the HUD camera so it is unaffected by main
// camera scroll/zoom.  The knob is constrained to a circular dead-zone + max-
// displacement range and the final output supports 8-way movement (4 cardinals + 4 diagonals).

const BASE_RADIUS = 60;       // outer ring visual radius (base diameter = 120px)
const TOUCH_ACCEPT_RADIUS = 80; // larger touch acceptance zone (visual base stays 60px)
const KNOB_RADIUS = 24;       // knob radius (knob diameter = 48px)
const MAX_DISPLACEMENT = 40;  // max distance knob travels from center
const DEAD_ZONE_RADIUS = 8;   // below this → STOP command (≈ 22% of max)

// Alpha values for the pixel-art semi-transparent style
const BASE_ALPHA = 0.35;
const KNOB_ALPHA = 0.65;
const ACTIVE_ALPHA = 0.80;

const STOP_COMMAND: MoveCommand = {left: false, right: false, up: false, down: false};

// 8-way sector boundary: 22.5° from each axis (45° per sector)
const SECTOR_BOUNDARY = Math.tan(Math.PI / 8); // tan(22.5°) ≈ 0.414

/**
 * Converts joystick displacement to an 8-way MoveCommand using angle-based sectors.
 * Each of the 8 directions gets an equal 45° sector, giving cardinals the same width
 * as diagonals. This makes it much easier to hit pure cardinal directions from a diagonal.
 * Returns STOP if the total displacement is within the dead zone.
 */
export function joystickToCommand(dx: number, dy: number): MoveCommand {
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < DEAD_ZONE_RADIUS) return {...STOP_COMMAND};

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    // Ratio test: if minor axis / major axis < tan(22.5°), it's a cardinal direction
    const isHorizontalDominant = absDx > absDy;
    const ratio = isHorizontalDominant ? absDy / absDx : absDx / absDy;
    const isDiagonal = ratio >= SECTOR_BOUNDARY;

    if (isHorizontalDominant) {
        return {
            left: dx < 0,
            right: dx > 0,
            up: isDiagonal && dy < 0,
            down: isDiagonal && dy > 0,
        };
    }
    return {
        left: isDiagonal && dx < 0,
        right: isDiagonal && dx > 0,
        up: dy < 0,
        down: dy > 0,
    };
}

export class VirtualJoystick {
    private _scene: Phaser.Scene;
    private _baseX: number;
    private _baseY: number;

    private _baseCircle: Phaser.GameObjects.Arc;
    private _knob: Phaser.GameObjects.Arc;

    private _activePointerId: number = -1;
    private _knobDx: number = 0;
    private _knobDy: number = 0;

    constructor(scene: Phaser.Scene, x: number, y: number) {
        this._scene = scene;
        this._baseX = x;
        this._baseY = y;
        this._createGraphics();
        this._registerPointerEvents();
    }

    private _createGraphics(): void {
        // Outer ring (base)
        this._baseCircle = this._scene.add.circle(
            this._baseX, this._baseY, BASE_RADIUS,
            0x888888, BASE_ALPHA
        ).setDepth(150).setScrollFactor(0);

        // Add a slightly visible border to the base ring
        // Phaser Arc doesn't have a strokeAlpha property; draw an outline arc
        const outline = this._scene.add.arc(
            this._baseX, this._baseY, BASE_RADIUS,
            0, 360, false, 0xaaaaaa, 0.5
        ).setDepth(150).setScrollFactor(0);
        outline.setStrokeStyle(2, 0xffffff, 0.4);
        outline.setFillStyle(); // no fill — outline only

        // Knob
        this._knob = this._scene.add.circle(
            this._baseX, this._baseY, KNOB_RADIUS,
            0xffffff, KNOB_ALPHA
        ).setDepth(151).setScrollFactor(0);
    }

    private _registerPointerEvents(): void {
        this._scene.input.on('pointerdown', this._onPointerDown, this);
        this._scene.input.on('pointermove', this._onPointerMove, this);
        this._scene.input.on('pointerup', this._onPointerUp, this);
        this._scene.input.on('pointercancel', this._onPointerUp, this);
    }

    private _isInJoystickZone(x: number, y: number): boolean {
        // Accept pointer if it lands within the base circle
        const dx = x - this._baseX;
        const dy = y - this._baseY;
        return Math.sqrt(dx * dx + dy * dy) <= TOUCH_ACCEPT_RADIUS;
    }

    private _onPointerDown(pointer: Phaser.Input.Pointer): void {
        // Only claim this pointer if it's in the joystick zone and no pointer is active
        if (this._activePointerId !== -1) return;
        if (!this._isInJoystickZone(pointer.x, pointer.y)) return;
        this._activePointerId = pointer.id;
        this._updateKnob(pointer.x, pointer.y);
        this._baseCircle.setAlpha(ACTIVE_ALPHA);
    }

    private _onPointerMove(pointer: Phaser.Input.Pointer): void {
        if (pointer.id !== this._activePointerId) return;
        this._updateKnob(pointer.x, pointer.y);
    }

    private _onPointerUp(pointer: Phaser.Input.Pointer): void {
        if (pointer.id !== this._activePointerId) return;
        this._activePointerId = -1;
        this._knobDx = 0;
        this._knobDy = 0;
        this._knob.setPosition(this._baseX, this._baseY);
        this._baseCircle.setAlpha(BASE_ALPHA);
    }

    private _updateKnob(pointerX: number, pointerY: number): void {
        let dx = pointerX - this._baseX;
        let dy = pointerY - this._baseY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > MAX_DISPLACEMENT) {
            const scale = MAX_DISPLACEMENT / dist;
            dx *= scale;
            dy *= scale;
        }

        this._knobDx = dx;
        this._knobDy = dy;
        this._knob.setPosition(this._baseX + dx, this._baseY + dy);
    }

    /** Returns the current 8-way MoveCommand derived from knob position. */
    getCommand(): MoveCommand {
        return joystickToCommand(this._knobDx, this._knobDy);
    }

    /** True when a pointer is actively touching the joystick. */
    isActive(): boolean {
        return this._activePointerId !== -1;
    }

    destroy(): void {
        this._scene.input.off('pointerdown', this._onPointerDown, this);
        this._scene.input.off('pointermove', this._onPointerMove, this);
        this._scene.input.off('pointerup', this._onPointerUp, this);
        this._scene.input.off('pointercancel', this._onPointerUp, this);
        this._baseCircle.destroy();
        this._knob.destroy();
    }
}
